import { useCallback, useEffect, useMemo } from 'react';
import { fetchEvalConfigs } from '@queries/evalConfigs';
import type { EvalRunDetail, EvalRunSummary } from '@queries/evalRuns';
import { fetchEvalBudget, fetchEvalRun, fetchEvalRuns } from '@queries/evalRuns';
import { useQuery } from '@tanstack/react-query';

import { POLL_INTERVAL_MS } from '../constants';
import type { EvalConfigOption, EvalRunStatus } from '../types';
import { RUN_IS_TERMINAL } from '../types';

export const EVAL_RUNS_LIST_KEY = (submissionId: string) => ['pbqa-eval', 'runs', submissionId] as const;
export const EVAL_RUN_DETAIL_KEY = (submissionId: string, runId: string) =>
  ['pbqa-eval', 'run', submissionId, runId] as const;
export const EVAL_CONFIGS_PICKER_KEY = ['pbqa-eval', 'configs'] as const;
export const EVAL_BUDGET_KEY = ['pbqa-eval', 'budget'] as const;

/**
 * Poll the run detail while anything about it can still change: the review
 * run itself, or the latest AI resolution check. A check runs long after its
 * (terminal) review run and writes both its own progress and per-finding
 * verdicts as it goes, so it needs to keep the query alive on its own.
 *
 * `listSaysActive` is the run list's view of the same run. The two are
 * fetched independently, so the detail keeps polling for as long as either
 * says the run is still going — the scan screen must not be left behind by
 * a detail fetch that missed the last transition.
 */
export const detailPollInterval = (detail?: EvalRunDetail, listSaysActive = false): number | false => {
  if (!detail) return false;
  const runActive = !RUN_IS_TERMINAL[detail.run.status as EvalRunStatus];
  const latestCheck = detail.resolution_checks?.[0];
  const checkActive = latestCheck ? !RUN_IS_TERMINAL[latestCheck.status as EvalRunStatus] : false;
  return runActive || checkActive || listSaysActive ? POLL_INTERVAL_MS : false;
};

interface UseEvalRunsDataArgs {
  submissionId: string;
  activeRunId?: string;
  /** Disables polling until the drawer is actually open to save requests. */
  enabled: boolean;
  /**
   * Whether to load the config list. The configs route is internal-only
   * (`pbqa_eval`): a broker gets a 403 there, and never needs the list —
   * their run button posts without a config id and the server picks the
   * champion.
   */
  fetchConfigs: boolean;
}

/**
 * Bundles the four network calls the PBQA drawer needs:
 *   1. `runs`    — list of all runs for the submission (for the RunSwitcher)
 *   2. `detail`  — full detail of the currently-selected run
 *   3. `configs` — available eval configs (for the "Run evaluation" picker)
 *   4. `budget`  — the caller's daily token budget (gates "Run evaluation")
 *
 * Polling on the list + active run is driven by whether the active run is
 * in a non-terminal state. Once everything terminates we stop polling to
 * keep the Dynamo/Bedrock traffic low.
 */
export function useEvalRunsData({ submissionId, activeRunId, enabled, fetchConfigs }: UseEvalRunsDataArgs) {
  const runsQuery = useQuery({
    queryKey: EVAL_RUNS_LIST_KEY(submissionId),
    queryFn: () => fetchEvalRuns(submissionId),
    enabled: enabled && Boolean(submissionId),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const runs = (query.state.data ?? []) as EvalRunSummary[];
      const hasActive = runs.some((r) => !RUN_IS_TERMINAL[r.status as EvalRunStatus]);
      return hasActive ? POLL_INTERVAL_MS : false;
    },
  });

  // The budget moves while a run is spending, so it polls on the same
  // cadence as the list whenever a run is active; otherwise it is fetched
  // once per drawer open (staleTime 0 + mount) and on explicit invalidation.
  const hasActiveRun = (runsQuery.data ?? []).some((r) => !RUN_IS_TERMINAL[r.status as EvalRunStatus]);
  const budgetQuery = useQuery({
    queryKey: EVAL_BUDGET_KEY,
    queryFn: () => fetchEvalBudget(submissionId),
    enabled: enabled && Boolean(submissionId),
    refetchOnWindowFocus: false,
    refetchInterval: hasActiveRun ? POLL_INTERVAL_MS : false,
  });

  const activeRunInList = (runsQuery.data ?? []).find((r) => r.id === activeRunId);
  const listSaysActive = activeRunInList ? !RUN_IS_TERMINAL[activeRunInList.status as EvalRunStatus] : false;

  const detailQuery = useQuery({
    queryKey: activeRunId ? EVAL_RUN_DETAIL_KEY(submissionId, activeRunId) : ['pbqa-eval', 'run', 'missing'],
    queryFn: () => fetchEvalRun(submissionId, activeRunId!),
    enabled: enabled && Boolean(activeRunId),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => detailPollInterval(query.state.data as EvalRunDetail | undefined, listSaysActive),
  });

  // The list learned the run finished before the detail did (the header polls
  // the list on its own): fetch the detail now rather than on its next tick,
  // so the scan screen gives way to the results as soon as they exist.
  const detailSaysActive = detailQuery.data ? !RUN_IS_TERMINAL[detailQuery.data.run.status as EvalRunStatus] : false;
  const detailBehindList = Boolean(activeRunInList) && !listSaysActive && detailSaysActive;
  // `refetch()` ignores `enabled`: called with no run selected it would still
  // run the queryFn and request `/eval_runs/undefined`, which the API rejects
  // as an invalid id (the toast a broker saw on a contract's first scan).
  const detailRefetch = detailQuery.refetch;
  const refetchDetail = useCallback(
    () => (activeRunId ? detailRefetch() : Promise.resolve(undefined)),
    [activeRunId, detailRefetch],
  );
  useEffect(() => {
    if (detailBehindList && !detailQuery.isFetching) refetchDetail();
  }, [detailBehindList, detailQuery.isFetching, refetchDetail]);

  const configsQuery = useQuery({
    queryKey: EVAL_CONFIGS_PICKER_KEY,
    queryFn: fetchEvalConfigs,
    enabled: enabled && fetchConfigs,
    refetchOnWindowFocus: false,
  });

  const configs = useMemo<EvalConfigOption[]>(
    () =>
      (configsQuery.data ?? [])
        .filter((c) => !c.archived)
        .map((c) => ({
          id: c.id,
          name: c.name,
          workflow_type: c.workflow_type,
          current_version_number: c.current_version_number,
          is_champion: c.is_champion,
        })),
    [configsQuery.data],
  );

  return {
    runs: runsQuery.data ?? [],
    runsLoading: runsQuery.isLoading,
    runsError: runsQuery.error,
    detail: detailQuery.data,
    detailLoading: detailQuery.isLoading,
    detailError: detailQuery.error,
    configs,
    configsLoading: configsQuery.isLoading,
    // `undefined` while loading or on error: the form treats that as "no
    // reason to block", so a budget fetch failure never locks the button.
    budget: budgetQuery.data,
    refetchRuns: runsQuery.refetch,
    refetchDetail,
  };
}
